import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { TourProvider } from "./contexts/TourContext";
import DocumentTitle from "./components/DocumentTitle";
import { PermissionsProvider, usePermissions } from "./contexts/PermissionsContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import EmployeeLoginPage from "./pages/EmployeeLoginPage";
import EmployeePortalPage from "./pages/EmployeePortalPage";
import AuthCallback from "./pages/AuthCallback";
import TenantSignupPage from "./pages/TenantSignupPage";
import TenantLoginPage from "./pages/TenantLoginPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import MarketingSitePage from "./pages/MarketingSitePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AppLayout from "./components/layout/AppLayout";
import StylistLayout from "./components/layout/StylistLayout";
import ClientSiteLayout from "./components/layout/ClientSiteLayout";
import ClientHomePage from "./pages/client/ClientHomePage";
import ClientServicesPage from "./pages/client/ClientServicesPage";
import ClientTeamPage from "./pages/client/ClientTeamPage";
import ClientBookingPage from "./pages/client/ClientBookingPage";
import ClockKioskPage from "./pages/ClockKioskPage";
import CashDrawerKioskPage from "./pages/CashDrawerKioskPage";
import ColorBarKioskPage from "./pages/ColorBarKioskPage";
import ColorLibraryPage from "./pages/ColorLibraryPage";
import CashDrawerHistoryPage from "./pages/CashDrawerHistoryPage";
import PublicFormPage from "./pages/PublicFormPage";
import EventsPage from "./pages/EventsPage";
import SchedulePage from "./pages/SchedulePage";
import OperationsPage from "./pages/OperationsPage";
import TasksBoardPage from "./pages/TasksBoardPage";
import BillingPage from "./pages/BillingPage";
import PublicEventIntakePage from "./pages/PublicEventIntakePage";
import PublicOpenIntakePage from "./pages/PublicOpenIntakePage";
import DashboardPage from "./pages/DashboardPage";
import CalendarPage from "./pages/CalendarPage";
import ClientsPage from "./pages/ClientsPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import POSPage from "./pages/POSPage";
import ReportsPage from "./pages/ReportsPage";
import StaffPage from "./pages/StaffPage";
import StaffMemberPage from "./pages/StaffMemberPage";
import SettingsPage from "./pages/SettingsPage";
import MarketingPage from "./pages/MarketingPage";
import MembershipsPage from "./pages/MembershipsPage";
import TimesheetsPage from "./pages/TimesheetsPage";
import InventoryPage from "./pages/InventoryPage";
import GiftCardsPage from "./pages/GiftCardsPage";
import NotificationsPage from "./pages/NotificationsPage";
import FormsPage from "./pages/FormsPage";
import ReviewsPage from "./pages/ReviewsPage";
import ClientScoreDashboard from "./pages/ClientScoreDashboard";
import ScoreAlertsPage from "./pages/ScoreAlertsPage";
import TeamHubPage from "./pages/TeamHubPage";
import TeamPerformancePage from "./pages/TeamPerformancePage";
import TrainingStudioPage from "./pages/TrainingStudioPage";
import NoShowRiskPage from "./pages/NoShowRiskPage";
import ShiftBriefingPage from "./pages/ShiftBriefingPage";
import WaitlistPage from "./pages/WaitlistPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import ExecutiveDashboardPage from "./pages/ExecutiveDashboardPage";
import RebookPage from "./pages/RebookPage";
import WhiteLabelPage from "./pages/WhiteLabelPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import WebhooksPage from "./pages/WebhooksPage";
import ReferralPage from "./pages/ReferralPage";
import CommandCenterPage from "./pages/CommandCenterPage";
import LocationPulseBoardPage from "./pages/LocationPulseBoardPage";
import StylistDayPage from "./pages/stylist/StylistDayPage";
import StylistSchedulePage from "./pages/stylist/StylistSchedulePage";
import StylistEarningsPage from "./pages/stylist/StylistEarningsPage";
import StylistClientsPage from "./pages/stylist/StylistClientsPage";
import StylistTrainingPage from "./pages/stylist/StylistTrainingPage";
import StylistProductsPage from "./pages/stylist/StylistProductsPage";
import HelpCenterPage from "./pages/HelpCenterPage";

const STAFF_ROLES = ["stylist", "esthetician", "nail_tech", "colorist", "front_desk"];

function ProtectedRoute({ children, module }) {
  const { canView, loading } = usePermissions();
  if (loading) return null;
  if (module && !canView(module)) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-8" data-testid="access-denied">
        <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
          <svg className="w-7 h-7 text-rose-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </div>
        <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-1">Access Restricted</h2>
        <p className="text-sm text-slate-400 font-['Manrope'] max-w-sm">You don't have permission to view this module. Contact your manager to request access.</p>
      </div>
    );
  }
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && STAFF_ROLES.includes(user.role)) {
    return <Navigate to="/my-portal" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === "platform_admin") return <Navigate to="/platform" replace />;
    if (STAFF_ROLES.includes(user.role)) return <Navigate to="/my-portal" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <MarketingSitePage />;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - MUST be synchronous during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Marketing landing */}
      <Route path="/" element={<HomeRoute />} />
      {/* Multi-tenant signup + platform admin */}
      <Route path="/signup" element={<TenantSignupPage />} />
      <Route path="/platform" element={<PlatformAdminPage />} />
      <Route path="/platform/login" element={<LoginPage platformMode />} />
      <Route path="/t/:slug/login" element={<TenantLoginPage />} />
      {/* Classic login (non-tenant) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/employee/login" element={<EmployeeLoginPage />} />
      <Route path="/employee" element={<EmployeePortalPage />} />
      <Route path="/clock" element={<ClockKioskPage />} />
      <Route path="/cash-drawer" element={<CashDrawerKioskPage />} />
      <Route path="/color-bar" element={<ColorBarKioskPage />} />
      <Route path="/f/:token" element={<PublicFormPage />} />
      <Route path="/events/intake/:token" element={<PublicEventIntakePage />} />
      <Route path="/events/inquire/:slug" element={<PublicOpenIntakePage />} />
      <Route path="/book" element={<Navigate to="/salon/book" replace />} />
      {/* Client-facing public site */}
      <Route path="/salon" element={<ClientSiteLayout />}>
        <Route index element={<ClientHomePage />} />
        <Route path="services" element={<ClientServicesPage />} />
        <Route path="team" element={<ClientTeamPage />} />
        <Route path="book" element={<ClientBookingPage />} />
      </Route>
      {/* Stylist Portal */}
      <Route element={<StylistLayout />}>
        <Route path="/my-portal" element={<StylistDayPage />} />
        <Route path="/my-portal/schedule" element={<StylistSchedulePage />} />
        <Route path="/my-portal/earnings" element={<StylistEarningsPage />} />
        <Route path="/my-portal/clients" element={<StylistClientsPage />} />
        <Route path="/my-portal/training" element={<StylistTrainingPage />} />
        <Route path="/my-portal/products" element={<StylistProductsPage />} />
      </Route>
      {/* Admin / Owner */}
      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<ProtectedRoute module="dashboard"><DashboardPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute module="calendar"><CalendarPage /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute module="events"><EventsPage /></ProtectedRoute>} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/tasks-board" element={<TasksBoardPage />} />
        <Route path="/color-library" element={<ProtectedRoute><ColorLibraryPage /></ProtectedRoute>} />
        <Route path="/settings/billing" element={<BillingPage />} />
        <Route path="/clients" element={<ProtectedRoute module="clients"><ClientsPage /></ProtectedRoute>} />
        <Route path="/clients/:clientId" element={<ProtectedRoute module="clients"><ClientProfilePage /></ProtectedRoute>} />
        <Route path="/client-scores" element={<ProtectedRoute module="client_scores"><ClientScoreDashboard /></ProtectedRoute>} />
        <Route path="/score-alerts" element={<ProtectedRoute module="score_alerts"><ScoreAlertsPage /></ProtectedRoute>} />
        <Route path="/team-hub" element={<ProtectedRoute module="team_hub"><TeamHubPage /></ProtectedRoute>} />
        <Route path="/team-performance" element={<ProtectedRoute module="team_performance"><TeamPerformancePage /></ProtectedRoute>} />
        <Route path="/training" element={<ProtectedRoute module="training"><TrainingStudioPage /></ProtectedRoute>} />
        <Route path="/timesheets" element={<ProtectedRoute module="timeclock"><TimesheetsPage /></ProtectedRoute>} />
        <Route path="/timeclock" element={<Navigate to="/timesheets" replace />} />
        <Route path="/noshow-risk" element={<ProtectedRoute module="noshow_risk"><NoShowRiskPage /></ProtectedRoute>} />
        <Route path="/shift-briefing" element={<ProtectedRoute module="shift_briefing"><ShiftBriefingPage /></ProtectedRoute>} />
        <Route path="/waitlist" element={<ProtectedRoute module="waitlist"><WaitlistPage /></ProtectedRoute>} />
        <Route path="/loyalty" element={<ProtectedRoute module="loyalty"><LoyaltyPage /></ProtectedRoute>} />
        <Route path="/executive" element={<ProtectedRoute module="executive"><ExecutiveDashboardPage /></ProtectedRoute>} />
        <Route path="/rebook" element={<ProtectedRoute module="rebook"><RebookPage /></ProtectedRoute>} />
        <Route path="/white-label" element={<ProtectedRoute module="white_label"><WhiteLabelPage /></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute module="integrations"><IntegrationsPage /></ProtectedRoute>} />
        <Route path="/webhooks" element={<ProtectedRoute module="webhooks"><WebhooksPage /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute module="referrals"><ReferralPage /></ProtectedRoute>} />
        <Route path="/command-center" element={<ProtectedRoute module="command_center"><CommandCenterPage /></ProtectedRoute>} />
        <Route path="/location-pulse" element={<ProtectedRoute module="location_pulse"><LocationPulseBoardPage /></ProtectedRoute>} />
        <Route path="/pos" element={<ProtectedRoute module="pos"><POSPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute module="reports"><ReportsPage /></ProtectedRoute>} />
        <Route path="/reports/cash-drawer" element={<ProtectedRoute module="reports"><CashDrawerHistoryPage /></ProtectedRoute>} />
        <Route path="/staff" element={<ProtectedRoute module="staff"><StaffPage /></ProtectedRoute>} />
        <Route path="/staff/:userId" element={<ProtectedRoute><StaffMemberPage /></ProtectedRoute>} />
        <Route path="/marketing" element={<ProtectedRoute module="marketing"><MarketingPage /></ProtectedRoute>} />
        <Route path="/memberships" element={<ProtectedRoute module="memberships"><MembershipsPage /></ProtectedRoute>} />
        <Route path="/payroll" element={<Navigate to="/timesheets?tab=payroll" replace />} />
        <Route path="/inventory" element={<ProtectedRoute module="inventory"><InventoryPage /></ProtectedRoute>} />
        <Route path="/gift-cards" element={<ProtectedRoute module="gift_cards"><GiftCardsPage /></ProtectedRoute>} />
        <Route path="/automations" element={<ProtectedRoute module="automations"><NotificationsPage /></ProtectedRoute>} />
        <Route path="/forms" element={<ProtectedRoute module="forms"><FormsPage /></ProtectedRoute>} />
        <Route path="/reviews" element={<ProtectedRoute module="reviews"><ReviewsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute module="settings"><SettingsPage /></ProtectedRoute>} />
        <Route path="/help" element={<HelpCenterPage />} />
      </Route>
      <Route path="*" element={<RoleRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <PermissionsProvider>
            <TourProvider>
              <DocumentTitle />
              <AppRouter />
              <Toaster position="bottom-right" />
            </TourProvider>
          </PermissionsProvider>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
